import React from 'react';
import {
  CapaServiceType,
  CapaWithdrawnConnectGroupChatRoom,
  CapaWithdrawnPartnerChatRoom,
  CapaWithdrawnUserChatRoom,
  CapaWithdrawOutput
} from '@generated/graphql';
import CapaAvatar from './Avatar';
import { CustomizedButton, CustomizedTypography } from '@components/Customized';
import { ChevronRightOutlined } from '@material-ui/icons';
import { Image } from '@components/Image';
import AppConfig from '@config';
import { DefaultCompanyThumbnail } from '@assets';
import { Pagination } from '@material-ui/lab';

interface Props {
  data?: CapaWithdrawOutput;
  serviceTab: CapaServiceType;
  pagination: {
    offset: number;
    limit: number;
  };
  onClickPage: (value: number) => void;
}

const EmptyScreen = ({ serviceTab }: Pick<Props, 'serviceTab'>) => {
  return (
    <div className="empty-screen">
      <CustomizedTypography
        variant={'body2'}
        fontWeight="normal"
        color="textSecondary"
      >
        {serviceTab === CapaServiceType.Connect
          ? '소유자인 그룹이 없습니다.'
          : '상담중인 채팅이 없습니다.'}
      </CustomizedTypography>
    </div>
  );
};

export const ConnectChatRooms = ({
  data,
  serviceTab,
  pagination,
  onClickPage
}: Props) => {
  const isEmptyQuotationConnect =
    data?.consultingChatRoomsCount &&
    data.consultingChatRoomsCount.length > 0 &&
    data.consultingChatRoomsCount.filter(
      (room) =>
        room.capaService === CapaServiceType.Connect && room.totalCount > 0
    ).length > 0
      ? false
      : true;

  return (
    <>
      <div className="open-chat-list-root">
        {isEmptyQuotationConnect ? (
          <EmptyScreen serviceTab={serviceTab} />
        ) : (
          <>
            {(
              data?.consultingChatRooms
                ?.chatRooms as CapaWithdrawnConnectGroupChatRoom[]
            )?.map((v, index) => {
              return (
                <div className="open-chat-list-item" key={index}>
                  <CapaAvatar
                    data={(v?.users ?? []).slice(0, 4)}
                    isChecked={false}
                  />
                  <div className="company-info group-name">
                    <CustomizedTypography
                      variant={'body1'}
                      fontWeight="500"
                      color="textPrimary"
                    >
                      {v.groupName}
                    </CustomizedTypography>
                  </div>
                  <CustomizedButton
                    className="go-chat"
                    variant="text"
                    color="primary"
                    endIcon={<ChevronRightOutlined />}
                    onClick={() => {
                      window.open(
                        `${AppConfig.connectUrl}/group-room?chatroomId=${v.chatRoomId}&groupId=${v.groupId}&firstItem=true`
                      );
                    }}
                  >
                    그룹 내용 보기
                  </CustomizedButton>
                </div>
              );
            })}
          </>
        )}
      </div>
      <Pagination
        hidePrevButton
        hideNextButton
        className={'chat-pagination-root'}
        count={
          !isEmptyQuotationConnect
            ? Math.ceil(
                (data?.consultingChatRoomsCount.filter(
                  (v) => v.capaService === serviceTab
                )[0]?.totalCount ?? 0) / 5
              )
            : 1
        }
        page={pagination.offset / 5 + 1}
        onChange={(_, value) => onClickPage(value)}
      />
    </>
  );
};

export const QuotationChatRooms = ({
  data,
  serviceTab,
  pagination,
  onClickPage
}: Props) => {
  const isEmptyQuotationChat =
    data?.consultingChatRoomsCount &&
    data.consultingChatRoomsCount.length > 0 &&
    data.consultingChatRoomsCount.filter(
      (room) =>
        room.capaService === CapaServiceType.Quotation && room.totalCount > 0
    ).length > 0
      ? false
      : true;

  return (
    <>
      <div className="open-chat-list-root">
        {isEmptyQuotationChat ? (
          <EmptyScreen serviceTab={serviceTab} />
        ) : (
          <>
            {(
              data?.consultingChatRooms
                ?.chatRooms as CapaWithdrawnUserChatRoom[]
            )?.map((v, index) => {
              return (
                <div className="open-chat-list-item" key={index}>
                  <Image
                    className="thumbnail"
                    alt=""
                    onError={(e) => {
                      e.currentTarget.src = DefaultCompanyThumbnail;
                    }}
                    src={`${AppConfig.fileApiUrl}/file/${v.profileId}`}
                  />
                  <div className="company-info">
                    <CustomizedTypography
                      variant={'body1'}
                      fontWeight="500"
                      color="textPrimary"
                    >
                      {v.organizationName}
                    </CustomizedTypography>
                    <CustomizedTypography
                      variant={'body2'}
                      fontWeight="500"
                      color="textSecondary"
                      className="message-preview"
                    >
                      {v.projectName}
                    </CustomizedTypography>
                  </div>
                  <CustomizedButton
                    className="go-chat"
                    variant="text"
                    color="primary"
                    endIcon={<ChevronRightOutlined />}
                    onClick={() => {
                      window.open(`/chat/${v.chatRoomId}`);
                    }}
                  >
                    채팅 내용 보기
                  </CustomizedButton>
                </div>
              );
            })}
          </>
        )}
      </div>
      <Pagination
        hidePrevButton
        hideNextButton
        className={'chat-pagination-root'}
        count={
          !isEmptyQuotationChat
            ? Math.ceil(
                (data?.consultingChatRoomsCount.filter(
                  (v) => v.capaService === serviceTab
                )[0]?.totalCount ?? 0) / 5
              )
            : 1
        }
        page={pagination.offset / 5 + 1}
        onChange={(_, value) => onClickPage(value)}
      />
    </>
  );
};

export const QuotationPartnerChatRooms = ({
  data,
  serviceTab,
  pagination,
  onClickPage
}: Props) => {
  const isEmptyQuotationPartnerChat =
    data?.consultingChatRoomsCount &&
    data.consultingChatRoomsCount.length > 0 &&
    data.consultingChatRoomsCount.filter(
      (room) =>
        room.capaService === CapaServiceType.QuotationPartner &&
        room.totalCount > 0
    ).length > 0
      ? false
      : true;

  return (
    <>
      <div className="open-chat-list-root">
        {isEmptyQuotationPartnerChat ? (
          <EmptyScreen serviceTab={serviceTab} />
        ) : (
          <>
            {(
              data?.consultingChatRooms
                ?.chatRooms as CapaWithdrawnPartnerChatRoom[]
            )?.map((v, index) => {
              return (
                <div className="open-chat-list-item" key={index}>
                  <Image
                    className="thumbnail"
                    alt=""
                    onError={(e) => {
                      e.currentTarget.src = DefaultCompanyThumbnail;
                    }}
                    src={`${AppConfig.fileApiUrl}/file/${v.thumbnailId}`}
                  />
                  <div className="company-info">
                    <CustomizedTypography
                      variant={'body1'}
                      fontWeight="500"
                      color="textPrimary"
                    >
                      {v.organizationName}
                    </CustomizedTypography>
                    <CustomizedTypography
                      variant={'body2'}
                      fontWeight="500"
                      color="textSecondary"
                      className="message-preview"
                    >
                      {v.projectName}
                    </CustomizedTypography>
                  </div>
                  <CustomizedButton
                    className="go-chat"
                    variant="text"
                    color="primary"
                    endIcon={<ChevronRightOutlined />}
                    onClick={() => {
                      window.open(`${AppConfig.partnersUrl}/chat/${v.rfqId}`);
                    }}
                  >
                    채팅 내용 보기
                  </CustomizedButton>
                </div>
              );
            })}
          </>
        )}
      </div>

      <Pagination
        hidePrevButton
        hideNextButton
        className={'chat-pagination-root'}
        count={
          isEmptyQuotationPartnerChat
            ? 1
            : Math.ceil(
                (data?.consultingChatRoomsCount.filter(
                  (v) => v.capaService === serviceTab
                )[0]?.totalCount ?? 0) / 5
              )
        }
        page={pagination.offset / 5 + 1}
        onChange={(_, value) => onClickPage(value)}
      />
    </>
  );
};
